import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowRight, Shield, Zap, BarChart3, RefreshCw, Star,
  Menu, X, ChevronRight, MessageCircle, CreditCard, Bitcoin, Copy, Loader2, Check
} from "lucide-react";

const PRODUCTS = [
  {
    id: "lead-scout", name: "Shopify Lead-Scout", tagline: "B2B Analytics Engine",
    price: 29, description: "Identify, score, and convert high-value B2B leads directly from your Shopify store.",
    icon: BarChart3,
    image: "/images/lead-scout.png",
    features: ["Real-time visitor company ID", "40+ behavioral signals", "CRM integration"]
  },
  {
    id: "ghost-recovery", name: "Ghost-Recovery v2", tagline: "Revenue Optimization",
    price: 19, description: "Recover abandoned carts with AI-powered multi-channel re-engagement.",
    icon: RefreshCw,
    image: "/images/ghost-recovery.png",
    features: ["AI abandoned cart recovery", "Email, SMS, push", "Revenue attribution"]
  },
  {
    id: "smart-sync", name: "Smart-Sync API", tagline: "Universal Data Bridge",
    price: 14, description: "Synchronize inventory, orders, and customer data across all platforms in real-time.",
    icon: Zap,
    image: "/images/smart-sync.png",
    features: ["Bi-directional sync", "50+ integrations", "99.99% uptime SLA"]
  },
  {
    id: "edge-guardian", name: "Edge Guardian", tagline: "Security & Performance",
    price: 9, description: "Enterprise-grade security and 3x performance optimization at the edge.",
    icon: Shield,
    image: "/images/edge-guardian.png",
    features: ["SSL & Security Scan", "Performance Analysis", "Security Header Audit"]
  }
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [blogs, setBlogs] = useState([]);
  const [demoForm, setDemoForm] = useState({ name: "", email: "", company: "", product_interest: "", message: "" });
  const [demoLoading, setDemoLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [cryptoSession, setCryptoSession] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const [testimonials, setTestimonials] = useState([]);
  const [newReview, setNewReview] = useState({ name: "", role: "", content: "", product: "General" });
  const [reviewLoading, setReviewLoading] = useState(false);

  useEffect(() => {
    API.get("/blog").then(r => setBlogs(r.data.slice(0, 3))).catch(() => {});
    API.get("/testimonials").then(r => setTestimonials(r.data)).catch(() => {});
  }, []);

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!newReview.name || !newReview.content) {
      toast.error("Name and comment are required");
      return;
    }
    setReviewLoading(true);
    try {
      await API.post("/testimonials", newReview);
      toast.success("Review submitted successfully! Thank you!");
      setNewReview({ name: "", role: "", content: "", product: "General" });
      const { data } = await API.get("/testimonials");
      setTestimonials(data);
    } catch (e) {
      toast.error("Failed to submit review");
    } finally {
      setReviewLoading(false);
    }
  };

  const handlePurchase = (productId) => {
    if (!user) { navigate("/login"); return; }
    setSelectedProductId(productId);
    setShowModal(true);
  };

  const handleInstantTrial = async () => {
    try {
      await API.post("/checkout/trial/activate", { product_id: selectedProductId });
      toast.success("14-Day Free Trial Activated! Redirecting to dashboard...");
      setShowModal(false);
      setTimeout(() => {
        navigate("/dashboard");
      }, 1000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleCryptoCheckout = async () => {
    try {
      const { data } = await API.post("/checkout/crypto/create", { product_id: selectedProductId });
      setCryptoSession(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Crypto checkout failed");
    }
  };

  const verifyCryptoPayment = async () => {
    if (!cryptoSession) return;
    setVerifying(true);
    try {
      const { data } = await API.get(`/checkout/crypto/verify/${cryptoSession.session_id}`);
      if (data.status === "paid") {
        toast.success("Payment verified successfully!");
        navigate(`/payment/success?session_id=${cryptoSession.session_id}`);
      } else {
        toast.info("Payment not found yet. It might take a few minutes for the network to confirm.");
      }
    } catch (e) {
      toast.error("Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const handleDemo = async (e) => {
    e.preventDefault();
    setDemoLoading(true);
    try {
      await API.post("/demo-request", demoForm);
      toast.success("Demo request submitted! We'll be in touch.");
      setDemoForm({ name: "", email: "", company: "", product_interest: "", message: "" });
    } catch { toast.error("Failed to submit. Try again."); }
    setDemoLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F0F0F0]">
      {/* Nav */}
      <nav className="fixed top-0 w-full bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#1E1E1E] z-50" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-heading text-xl font-black tracking-tighter uppercase" data-testid="nav-logo">
            NRD<span className="text-[#00FF41]">.</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="#products" className="text-sm font-medium text-slate-400 hover:text-[#00FF41] transition-colors" data-testid="nav-products">Products</a>
            <a href="#pricing" className="text-sm font-medium text-slate-400 hover:text-[#00FF41] transition-colors" data-testid="nav-pricing">Pricing</a>
            <a href="#testimonials" className="text-sm font-medium text-slate-400 hover:text-[#00FF41] transition-colors" data-testid="nav-testimonials">Testimonials</a>
            <Link to="/blog" className="text-sm font-medium text-slate-400 hover:text-[#00FF41] transition-colors" data-testid="nav-blog">Blog</Link>
            {user ? (
              <Link to="/dashboard" className="bg-[#00FF41] text-[#0A0A0A] px-5 py-2 text-sm font-bold hover:bg-[#00CC33] transition-colors" data-testid="nav-dashboard-btn">Dashboard</Link>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login" className="text-sm font-medium text-slate-400 hover:text-[#00FF41]" data-testid="nav-login">Sign In</Link>
                <Link to="/register" className="bg-[#00FF41] text-[#0A0A0A] px-5 py-2 text-sm font-bold hover:bg-[#00CC33] transition-colors" data-testid="nav-register-btn">Get Started</Link>
              </div>
            )}
          </div>
          <button className="md:hidden text-[#F0F0F0]" onClick={() => setMobileMenu(!mobileMenu)} data-testid="mobile-menu-toggle">
            {mobileMenu ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {mobileMenu && (
          <div className="md:hidden border-t border-[#1E1E1E] bg-[#0A0A0A] px-6 py-4 space-y-3">
            <a href="#products" className="block text-sm font-medium text-slate-300" onClick={() => setMobileMenu(false)}>Products</a>
            <a href="#pricing" className="block text-sm font-medium text-slate-300" onClick={() => setMobileMenu(false)}>Pricing</a>
            <Link to="/blog" className="block text-sm font-medium text-slate-300" onClick={() => setMobileMenu(false)}>Blog</Link>
            {user ? (
              <Link to="/dashboard" className="block bg-[#00FF41] text-[#0A0A0A] text-center px-5 py-2 text-sm font-bold">Dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="block text-sm font-medium text-slate-300">Sign In</Link>
                <Link to="/register" className="block bg-[#00FF41] text-[#0A0A0A] text-center px-5 py-2 text-sm font-bold">Get Started</Link>
              </>
            )}
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6" data-testid="hero-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="animate-fade-in-up">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#00FF41] mb-4" data-testid="hero-overline">NoRespectDigital // Commerce Automation Suite</p>
            <h1 className="font-heading text-5xl lg:text-6xl font-black tracking-tighter uppercase leading-[0.95] mb-6" data-testid="hero-title">
              AUTOMATE<br />YOUR COMMERCE<br /><span className="text-[#00FF41]">STACK</span>
            </h1>
            <p className="text-base text-slate-400 leading-relaxed max-w-lg mb-8" data-testid="hero-description">
              Four precision-engineered tools to identify leads, recover revenue, sync data, and harden security. Built for teams that ship.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register" className="inline-flex items-center gap-2 bg-[#00FF41] text-[#0A0A0A] px-6 py-3 text-sm font-bold hover:bg-[#00CC33] transition-colors animate-glow" data-testid="hero-cta-primary">
                Start Free Trial <ArrowRight size={16} />
              </Link>
              <a href="#products" className="inline-flex items-center gap-2 border border-[#00FF41] text-[#00FF41] px-6 py-3 text-sm font-medium hover:bg-[#00FF41] hover:text-[#0A0A0A] transition-colors" data-testid="hero-cta-secondary">
                View Products
              </a>
            </div>
          </div>
          <div className="relative animate-fade-in-up stagger-2">
            <div className="aspect-[4/3] overflow-hidden border border-[#1E1E1E]">
              <img
                src="https://images.unsplash.com/photo-1737505598998-693328b57ae3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHRlY2glMjBtaW5pbWFsJTIwcGF0dGVybnxlbnwwfHx8fDE3NzYxNzgyMDJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Abstract tech pattern" className="w-full h-full object-cover opacity-80"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-4 -right-4 bg-[#00FF41] text-[#0A0A0A] p-4 font-mono text-xs font-bold">
              <div>4 TOOLS</div>
              <div className="opacity-70">1 PLATFORM</div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Bento Grid */}
      <section id="products" className="py-20 px-6 border-t border-[#1E1E1E]" data-testid="products-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#00FF41] mb-3">Product Suite</p>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-16">The Complete Stack</h2>
          <div className="grid md:grid-cols-2 gap-0 border border-[#1E1E1E]">
            {PRODUCTS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={p.id}
                  className={`p-8 border-[#1E1E1E] ${i % 2 === 0 ? 'border-r' : ''} ${i < 2 ? 'border-b' : ''} group hover:bg-[#111111] premium-card-hover`}
                  data-testid={`product-card-${p.id}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-10 h-10 flex items-center justify-center border border-[#1E1E1E] group-hover:border-[#00FF41] group-hover:shadow-[4px_4px_0px_0px_rgba(0,255,65,0.6)] transition-all duration-150">
                      <Icon size={20} className="text-[#00FF41]" />
                    </div>
                    <span className="text-xs font-mono uppercase tracking-[0.2em] text-slate-600">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <div className="aspect-[16/9] mb-6 border border-[#1E1E1E] image-zoom-hover relative group-hover:border-[#00FF41]/40">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover opacity-70" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none"></div>
                  </div>
                  <h3 className="font-heading text-xl font-medium tracking-tight mb-1">{p.name}</h3>
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#00FF41] mb-3">{p.tagline}</p>
                  <p className="text-sm text-slate-400 mb-4">{p.description}</p>
                  <ul className="space-y-1 mb-6">
                    {p.features.map(f => (
                      <li key={f} className="text-xs text-slate-500 flex items-center gap-2">
                        <ChevronRight size={12} className="text-[#00FF41]" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Link to={`/product/${p.id}`} className="inline-flex items-center gap-2 text-sm font-medium text-[#00FF41] hover:text-[#00CC33] transition-colors" data-testid={`product-learn-more-${p.id}`}>
                    Learn More <ArrowRight size={14} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6 bg-[#111111] border-t border-[#1E1E1E]" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#00FF41] mb-3">Pricing</p>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-4">Transparent Pricing</h2>
          <p className="text-base text-slate-400 mb-16 max-w-lg">No hidden fees. Cancel anytime. All plans include setup support and 14-day free trial.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-[#1E1E1E] bg-[#0A0A0A]">
            {PRODUCTS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={p.id}
                  className={`p-6 ${i < 3 ? 'border-r border-[#1E1E1E]' : ''} flex flex-col`}
                  data-testid={`pricing-card-${p.id}`}
                >
                  <Icon size={20} className="mb-4 text-[#00FF41]" />
                  <h3 className="font-heading text-lg font-medium tracking-tight">{p.name}</h3>
                  <p className="text-xs text-slate-500 mb-4">{p.tagline}</p>
                  <div className="mb-6">
                    <span className="font-heading text-4xl font-black text-[#00FF41]">${p.price}</span>
                    <span className="text-sm text-slate-500">/mo</span>
                  </div>
                  <ul className="space-y-2 mb-6 flex-1">
                    {p.features.map(f => (
                      <li key={f} className="text-xs text-slate-400 flex items-start gap-2">
                        <ChevronRight size={12} className="text-[#00FF41] mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => handlePurchase(p.id)}
                    className="w-full bg-[#00FF41] text-[#0A0A0A] py-2.5 text-sm font-bold hover:bg-[#00CC33] transition-colors"
                    data-testid={`purchase-${p.id}-button`}
                  >
                    Subscribe Now
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 px-6 border-t border-[#1E1E1E]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#00FF41] mb-3">Reviews</p>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-16">Real Customer Feedback</h2>
          
          {testimonials.length === 0 ? (
            <div className="border border-[#1E1E1E] p-12 text-center bg-[#050505] mb-16">
              <p className="text-slate-500 font-mono text-sm">No reviews yet. Be the first to leave your feedback below!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-0 border border-[#1E1E1E] mb-16">
              {testimonials.map((t, i) => (
                <div key={i}
                  className="p-8 border-[#1E1E1E] border-r border-b last:border-r-0"
                  data-testid={`testimonial-${i}`}
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, j) => <Star key={j} size={14} className="fill-[#00FF41] text-[#00FF41]" />)}
                  </div>
                  <p className="text-base text-slate-300 mb-6 leading-relaxed">"{t.content}"</p>
                  <div className="flex items-center gap-3">
                    <img src={t.avatar} alt={t.name} className="w-10 h-10 object-cover grayscale border border-[#1E1E1E]" />
                    <div>
                      <p className="text-sm font-medium">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.role}</p>
                    </div>
                    <span className="ml-auto text-xs font-mono text-[#00FF41] uppercase tracking-wider">{t.product}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Leave a Review Form */}
          <div className="max-w-xl mx-auto border border-[#1E1E1E] p-8 bg-[#050505]">
            <h3 className="font-heading text-xl font-bold mb-4 uppercase tracking-wider text-center">Share Your Feedback</h3>
            <p className="text-xs text-slate-500 font-mono mb-6 text-center">Your review will appear instantly above. Real humans only!</p>
            <form onSubmit={handleReviewSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text" placeholder="Your Name" required value={newReview.name}
                  onChange={e => setNewReview(p => ({ ...p, name: e.target.value }))}
                  className="bg-[#111] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] placeholder-slate-600 focus:outline-none focus:border-[#00FF41] transition-colors"
                />
                <input
                  type="text" placeholder="Your Role / Company" value={newReview.role}
                  onChange={e => setNewReview(p => ({ ...p, role: e.target.value }))}
                  className="bg-[#111] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] placeholder-slate-600 focus:outline-none focus:border-[#00FF41] transition-colors"
                />
              </div>
              <select
                value={newReview.product}
                onChange={e => setNewReview(p => ({ ...p, product: e.target.value }))}
                className="w-full bg-[#111] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] focus:outline-none focus:border-[#00FF41] transition-colors"
              >
                <option value="General">General Platform</option>
                <option value="Lead-Scout">Shopify Lead-Scout</option>
                <option value="Ghost-Recovery">Ghost-Recovery v2</option>
                <option value="Smart-Sync">Smart-Sync API</option>
                <option value="Edge-Guardian">Edge Guardian</option>
              </select>
              <textarea
                placeholder="Write your review here..." required rows={4} value={newReview.content}
                onChange={e => setNewReview(p => ({ ...p, content: e.target.value }))}
                className="w-full bg-[#111] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] placeholder-slate-600 focus:outline-none focus:border-[#00FF41] transition-colors"
              />
              <button
                type="submit" disabled={reviewLoading}
                className="w-full bg-[#00FF41] text-[#0A0A0A] py-3 text-sm font-bold hover:bg-[#00CC33] transition-colors uppercase tracking-wider flex items-center justify-center gap-2"
              >
                {reviewLoading && <Loader2 size={16} className="animate-spin" />}
                Submit Review
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Blog Preview */}
      {blogs.length > 0 && (
        <section className="py-20 px-6 bg-[#111111] border-t border-[#1E1E1E]" data-testid="blog-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-16">
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#00FF41] mb-3">Blog</p>
                <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Latest Insights</h2>
              </div>
              <Link to="/blog" className="text-sm font-medium text-[#00FF41] hover:text-[#00CC33] flex items-center gap-1" data-testid="blog-view-all">
                View All <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-0 border border-[#1E1E1E] bg-[#0A0A0A]">
              {blogs.map((post, i) => (
                <div key={post.id} className={`p-6 ${i < 2 ? 'border-r border-[#1E1E1E]' : ''}`} data-testid={`blog-preview-${i}`}>
                  {post.cover_image && (
                    <div className="aspect-[16/9] overflow-hidden border border-[#1E1E1E] mb-4">
                      <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover opacity-70" />
                    </div>
                  )}
                  <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#00FF41]">{post.category}</span>
                  <h3 className="font-heading text-lg font-medium tracking-tight mt-2 mb-2">{post.title}</h3>
                  <p className="text-sm text-slate-400 mb-4">{post.excerpt}</p>
                  <Link to={`/blog/${post.id}`} className="text-sm font-medium text-[#00FF41] hover:text-[#00CC33] flex items-center gap-1" data-testid={`blog-read-${post.id}`}>
                    Read <ArrowRight size={14} />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Demo / Contact */}
      <section id="demo" className="py-20 px-6 border-t border-[#1E1E1E]" data-testid="demo-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#00FF41] mb-3">Get In Touch</p>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-4">Request a Live Demo</h2>
            <p className="text-base text-slate-400 mb-8">See how our automation suite can transform your commerce operations. Our team will walk you through a personalized demo.</p>
            <div className="flex items-center gap-3 p-4 border border-[#1E1E1E] bg-[#111111]">
              <MessageCircle size={20} className="text-[#00FF41]" />
              <div>
                <p className="text-sm font-medium">Live Chat Available</p>
                <p className="text-xs text-slate-500">Mon-Fri, 9AM-6PM EST</p>
              </div>
            </div>
          </div>
          <form onSubmit={handleDemo} className="space-y-4" data-testid="demo-form">
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text" placeholder="Name" required value={demoForm.name}
                onChange={e => setDemoForm(p => ({ ...p, name: e.target.value }))}
                className="bg-[#111111] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] placeholder-slate-600 focus:outline-none focus:border-[#00FF41] transition-colors"
                data-testid="demo-name-input"
              />
              <input
                type="email" placeholder="Email" required value={demoForm.email}
                onChange={e => setDemoForm(p => ({ ...p, email: e.target.value }))}
                className="bg-[#111111] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] placeholder-slate-600 focus:outline-none focus:border-[#00FF41] transition-colors"
                data-testid="demo-email-input"
              />
            </div>
            <input
              type="text" placeholder="Company (optional)" value={demoForm.company}
              onChange={e => setDemoForm(p => ({ ...p, company: e.target.value }))}
              className="w-full bg-[#111111] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] placeholder-slate-600 focus:outline-none focus:border-[#00FF41] transition-colors"
              data-testid="demo-company-input"
            />
            <select
              value={demoForm.product_interest} required
              onChange={e => setDemoForm(p => ({ ...p, product_interest: e.target.value }))}
              className="w-full bg-[#111111] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] focus:outline-none focus:border-[#00FF41] transition-colors"
              data-testid="demo-product-select"
            >
              <option value="">Select a product</option>
              {PRODUCTS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <textarea
              placeholder="Tell us about your needs (optional)" value={demoForm.message} rows={4}
              onChange={e => setDemoForm(p => ({ ...p, message: e.target.value }))}
              className="w-full bg-[#111111] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] placeholder-slate-600 focus:outline-none focus:border-[#00FF41] transition-colors resize-none"
              data-testid="demo-message-input"
            />
            <button
              type="submit" disabled={demoLoading}
              className="w-full bg-[#00FF41] text-[#0A0A0A] py-3 text-sm font-bold hover:bg-[#00CC33] transition-colors disabled:opacity-50"
              data-testid="demo-submit-button"
            >
              {demoLoading ? "Submitting..." : "Request Demo"}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1E1E1E] py-12 px-6 bg-[#050505]" data-testid="footer">
        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-8">
          <div>
            <p className="font-heading text-xl font-black tracking-tighter uppercase mb-2">NRD<span className="text-[#00FF41]">.</span></p>
            <p className="text-xs font-mono text-slate-600 mb-2">NoRespectDigital</p>
            <p className="text-sm text-slate-500">Commerce automation for teams that ship.</p>
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-600 mb-3">Products</p>
            {PRODUCTS.map(p => (
              <Link key={p.id} to={`/product/${p.id}`} className="block text-sm text-slate-500 hover:text-[#00FF41] mb-1.5 transition-colors">{p.name}</Link>
            ))}
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-600 mb-3">Company</p>
            <Link to="/blog" className="block text-sm text-slate-500 hover:text-[#00FF41] mb-1.5 transition-colors">Blog</Link>
            <a href="#demo" className="block text-sm text-slate-500 hover:text-[#00FF41] mb-1.5 transition-colors">Contact</a>
            <a href="#pricing" className="block text-sm text-slate-500 hover:text-[#00FF41] mb-1.5 transition-colors">Pricing</a>
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-600 mb-3">Legal</p>
            <p className="text-sm text-slate-500 mb-1.5">Privacy Policy</p>
            <p className="text-sm text-slate-500 mb-1.5">Terms of Service</p>
            <p className="text-xs text-slate-600 mt-4">&copy; 2025 NoRespectDigital. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-[#0A0A0A] border border-[#1E1E1E] p-8 max-w-md w-full relative">
            <button onClick={() => { setShowModal(false); setCryptoSession(null); }} className="absolute top-4 right-4 text-slate-500 hover:text-white">✕</button>
            <h2 className="font-heading text-2xl font-bold mb-6">Select Payment Method</h2>
            
            {!cryptoSession ? (
              <div className="space-y-4">
                <button onClick={handleInstantTrial} className="w-full flex items-center justify-between bg-[#00FF41]/10 border border-[#00FF41]/40 hover:bg-[#00FF41]/20 p-4 transition-colors group">
                  <span className="flex items-center gap-3 font-bold text-[#00FF41]"><Zap size={20} /> Start 14-Day Free Trial</span>
                  <ArrowRight size={16} className="text-[#00FF41] group-hover:translate-x-1 transition-transform" />
                </button>
                <button onClick={handleCryptoCheckout} className="w-full flex items-center justify-between bg-[#1E1E1E] hover:bg-[#2A2A2A] p-4 transition-colors">
                  <span className="flex items-center gap-3"><Bitcoin size={20} className="text-[#00FF41]" /> Crypto (USDT)</span>
                  <ArrowRight size={16} className="text-slate-500" />
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-yellow-500/10 border border-yellow-500/20 p-4 text-sm text-yellow-200">
                  <p className="font-bold mb-1">Important:</p>
                  <p>Send EXACTLY <strong>{cryptoSession.amount}</strong> {cryptoSession.currency} to the address below via the <strong>{cryptoSession.network}</strong> network.</p>
                </div>
                
                <div>
                  <label className="block text-xs text-slate-500 uppercase tracking-widest mb-2">Amount to send</label>
                  <div className="flex items-center justify-between bg-[#111] border border-[#1E1E1E] p-3">
                    <span className="font-mono text-[#00FF41] font-bold text-lg">{cryptoSession.amount} {cryptoSession.currency}</span>
                    <button onClick={() => copyToClipboard(cryptoSession.amount)} className="text-slate-400 hover:text-white"><Copy size={16}/></button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-slate-500 uppercase tracking-widest mb-2">Wallet Address ({cryptoSession.network})</label>
                  <div className="flex items-center justify-between bg-[#111] border border-[#1E1E1E] p-3 break-all">
                    <span className="font-mono text-sm text-slate-300">{cryptoSession.wallet_address}</span>
                    <button onClick={() => copyToClipboard(cryptoSession.wallet_address)} className="text-slate-400 hover:text-white ml-3 shrink-0"><Copy size={16}/></button>
                  </div>
                </div>

                <button 
                  onClick={verifyCryptoPayment} 
                  disabled={verifying}
                  className="w-full flex items-center justify-center gap-2 bg-[#00FF41] text-[#0A0A0A] py-3 font-bold hover:bg-[#00CC33] transition-colors disabled:opacity-50"
                >
                  {verifying ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                  I Have Paid (Verify)
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
